import { useMemo, useState } from 'react';
import type { PipelineMetricRow } from '@/modules/pipelines/data/metrics';
import styles from '@/modules/pipelines/components/deep-dive/asset-metrics/pipeline-asset-table.module.scss';

export type PipelineSortKey = 'name' | 'segments' | 'assets' | 'notices' | 'assetsWithNotices';
type SortDir = 'asc' | 'desc';

interface Column {
  key: PipelineSortKey;
  label: string;
  numeric: boolean;
  initialDir: SortDir;
}

const COLUMNS: Column[] = [
  { key: 'name', label: 'Pipeline', numeric: false, initialDir: 'asc' },
  { key: 'segments', label: 'Segments', numeric: true, initialDir: 'desc' },
  { key: 'assets', label: 'Assets', numeric: true, initialDir: 'desc' },
  { key: 'notices', label: 'Notices', numeric: true, initialDir: 'desc' },
  { key: 'assetsWithNotices', label: 'Affected', numeric: true, initialDir: 'desc' },
];

function compare(a: PipelineMetricRow, b: PipelineMetricRow, key: PipelineSortKey): number {
  if (key === 'name') return a.name.localeCompare(b.name);
  return a[key] - b[key];
}

export default function PipelineAssetTable({ rows }: { rows: PipelineMetricRow[] }) {
  const [sortKey, setSortKey] = useState<PipelineSortKey>('assets');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sorted = useMemo(() => {
    const factor = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort(
      (a, b) => compare(a, b, sortKey) * factor || a.name.localeCompare(b.name),
    );
  }, [rows, sortKey, sortDir]);

  const totalAssets = useMemo(() => rows.reduce((sum, row) => sum + row.assets, 0), [rows]);

  const toggle = (column: Column) => {
    if (column.key === sortKey) {
      setSortDir(current => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(column.key);
    setSortDir(column.initialDir);
  };

  if (!rows.length) {
    return <div className={styles.empty}>No pipelines returned by the graph.</div>;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>Assets by pipeline</span>
        <span className={styles.summary}>
          {rows.length.toLocaleString()} pipelines · {totalAssets.toLocaleString()} positioned
          assets
        </span>
      </div>

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map(column => {
                const active = column.key === sortKey;
                return (
                  <th
                    key={column.key}
                    className={column.numeric ? styles.numeric : undefined}
                    aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button
                      type={'button'}
                      className={styles.sortButton}
                      onClick={() => toggle(column)}
                    >
                      {column.label}
                      <span
                        className={active ? styles.sortArrow : styles.sortArrowIdle}
                        aria-hidden={'true'}
                      >
                        {active && sortDir === 'asc' ? '▲' : '▼'}
                      </span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map(row => (
              <tr key={row.id}>
                <td className={styles.name} title={row.name}>
                  {row.name}
                </td>
                <td className={`${styles.numeric} ${styles.count}`}>
                  {row.segments.toLocaleString()}
                </td>
                <td className={`${styles.numeric} ${styles.count}`}>
                  {row.assets.toLocaleString()}
                </td>
                <td className={`${styles.numeric} ${styles.count}`}>
                  {row.notices.toLocaleString()}
                </td>
                <td className={`${styles.numeric} ${styles.count}`}>
                  {row.assetsWithNotices.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
